import { Component, inject } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { GtmService } from '@df/module-gtm/gtm.service';
import { IAddressData } from '@df/session/api/address-book/address';
import { Customer } from '@df/session/api/customer';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { LocalBasket } from '../../basket/basket.decorator';
import { GTM_FORM_FIELD_ERROR_MESSAGE, LocalGtmService } from '../../module/gtm/gtm.service.decorator';
import { NewsletterCheckboxesComponent } from '../../newsletter/newsletter-checkboxes.component';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { AddressShortComponent } from '../../ui/display/address-short/address-short.component';
import { Section1Component } from '../../ui/display/section1/section1.component';
import { CheckoutAddresssComponent } from '../address/checkout-address.component';
import { CheckoutExpandableComponent } from '../expandable/checkout-expandable.component';
import { CheckoutFormComponent } from '../form/checkout-form.component';
import { CheckoutShippingMethodsComponent } from '../shipping-methods/checkout-shipping-methods.component';
import { CheckoutStorePickupComponent } from '../store-pickup/checkout-store-pickup.component';
import { CheckoutStorePickupStoreComponent } from '../store-pickup/store/checkout-store-pickup-store.component';
import { CheckoutSubmitComponent } from '../submit/checkout-submit.component';
import { CheckoutStepComponent } from './../step/checkout-step.component';
@Component({
    selector: 'checkout-delivery',
    templateUrl: './checkout-delivery.html',
    standalone: true,
    imports: [
        CheckoutExpandableComponent,
        Section1Component,
        CheckoutSubmitComponent,
        TranslationModule,
        ActionComponent,
        CheckoutShippingMethodsComponent,
        NewsletterCheckboxesComponent,
        ResultComponent,
        CheckoutAddresssComponent,
        CheckoutStorePickupComponent,
        CheckoutStorePickupStoreComponent,
        AddressShortComponent
    ]
})
export class CheckoutDeliveryComponent {
    private customer = inject(Customer);
    private formErrorSend = false;

    /**
     * expandable id
     */
    get expandableId(): string[] {
        return this.checkout.getExpandableId('delivery');
    }
    get expandableOff(): boolean {
        return this.checkout.isExpandableOff('delivery');
    }
    /**
     * Show only email input
     */
    get currentStepName(): string {
        return this.checkout.currentStep.name;
    }
    get step(): Checkout.IStep {
        return this.checkout.steps['delivery'];
    }
    get saveAdressStatus(): Status {
        return this.checkout.saveShippingAddressStatus;
    }
    get saveMethodStatus(): Status {
        return this.checkout.saveShippingMethodStatus;
    }
    get isValid(): boolean {
        return this.step.valid && !this.saveError;
    }
    get isBusy(): boolean {
        return this.saveAdressStatus.busy || this.saveMethodStatus.busy;
    }
    /**
     * Address data
     */
    get address(): IAddressData {
        return this.checkout.data.address.shipping;
    }
    get saveError(): string | undefined {
        return this.saveAdressStatus.error || this.saveMethodStatus.error;
    }

    constructor(
        public checkout: CheckoutFormComponent,
        public checkoutStepComponent: CheckoutStepComponent,
        private translationService: TranslationService,
        private readonly gtmService: GtmService
    ) {}

    get displayDeliveryTypeToggle(): boolean {
        return this.checkout.displayDeliverySelection;
    }
    toggleDeliverySelection(): void {
        this.checkout.toggleDeliverySelection();
    }
    get deliveryToggleLabel(): string {
        return this.checkout.isCollection ? 'Delivery' : 'Collection';
    }
    get sectionTitle(): string {
        let title: string;
        if (this.checkout.isCollection) {
            title = 'Collection';
        } else {
            const methodTitle = this.checkout.shippingMethod?.title;
            if (this.isCurrent || !methodTitle) {
                title = 'Delivery';
            } else {
                title = methodTitle;
            }
        }
        return this.translationService.translate(title);
    }
    get isCurrent(): boolean {
        return this.checkoutStepComponent.isCurrent;
    }
    get sectionIcon(): string {
        return this.checkout.isShipping ? 'delivery' : 'collection';
    }
    continue(): void {
        this.step.markSubmitted(true);
        if (this.isValid) {
            return this.checkout.goToNextStep('delivery');
        } else {
            if (!this.formErrorSend) {
                (this.gtmService as LocalGtmService).sendFormError(GTM_FORM_FIELD_ERROR_MESSAGE, 'checkout ' + this.step.name, 2);
            }
        }
    }
    /**
     * Checks if delivery is selected
     * @returns {boolean}
     */
    get isShipping(): boolean {
        return this.checkout.isShipping;
    }
    /**
     * Checks if click and collect is selected
     * @returns {boolean}
     */
    get isCollection(): boolean {
        return this.checkout.isCollection;
    }

    get disableCollection(): boolean {
        return !!(this.customer.basket as LocalBasket).hasGardenAssembly;
    }
}
